import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const menuItems = [
  { label: '인사말', href: '/greeting', active: true, internal: false },
  { label: '객실안내', href: '/rooms', active: false, internal: false },
  { label: '부대시설', href: '/facilities', active: false, internal: false },
  { label: '오시는 길', href: '/directions', active: false, internal: false },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const greetingSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': `${SITE_URL}/greeting`,
  url: `${SITE_URL}/greeting`,
  name: '인사말 | 영빈호텔',
  description: '서울 동대문 영빈호텔 대표이사 인사말. 집을 떠난 내집 같은 분위기로 고객 여러분을 맞이합니다.',
  isPartOf: { '@id': `${SITE_URL}/#website` },
  about: { '@id': `${SITE_URL}/#hotel` },
};

const GreetingPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = '인사말 | 영빈호텔 YOUNGBIN HOTEL';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '서울 동대문 영빈호텔 대표이사 인사말. 집을 떠난 내집 같은 분위기로 고객 여러분을 맞이합니다. 동대문 국제패션타운 인접, 지하철 2·3·4·5호선 연결.');
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-greeting';
    script.textContent = JSON.stringify(greetingSchema);
    document.head.appendChild(script);
    return () => {
      document.title = '영빈호텔 | YOUNGBIN HOTEL';
      if (metaDesc) metaDesc.setAttribute('content', '서울 중구 동대문 영빈호텔. 동대문 쇼핑상가 도보 10분, 사우나·무료와이파이·24시간 컨시어지 제공. TEL: 02-2277-1141');
      const el = document.getElementById('schema-greeting');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Sub Header */}
      <div className="pt-20 bg-stone-800">
        <div className="max-w-7xl mx-auto px-6 py-10">
          <p className="text-amber-400 text-xs tracking-[0.3em] uppercase mb-2">ABOUT HOTEL</p>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            인사말
          </h1>
        </div>
        {/* Sub Nav */}
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex gap-0">
              {menuItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  className={`px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer border-b-2 ${
                    item.active
                      ? 'border-amber-400 text-amber-400'
                      : 'border-transparent text-white/60 hover:text-white hover:border-white/40'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-20">

        {/* Hero Image */}
        <div className="w-full h-80 md:h-[460px] overflow-hidden rounded-lg mb-16">
          <img
            src="https://static.readdy.ai/image/8cbc9a1dc4b359b2d65998faa584ac95/61169bea203fb1408680167274008085.png"
            alt="영빈호텔 인사말"
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Greeting Text */}
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-10" style={{ fontFamily: "'Playfair Display', serif" }}>
            안녕하십니까
          </h2>

          {/* Text + Side Image Layout */}
          <div className="flex flex-col md:flex-row gap-10 mb-10">
            <div className="flex-1 space-y-8 text-gray-600 text-[15px] leading-[1.9]">
              <p>
                그동안 영빈호텔을 찾아주셨던 고객분들과 앞으로 모실 고객여러분께 감사에 말씀을 올립니다.<br />
                저희 영빈호텔의 캐치프레이즈인 <strong className="text-gray-800">"집을 떠난 내집 같은 분위기"</strong> 라는 구호에 맞게<br />
                전 종업원이 합심하여 만들어내는 정성을 참스럽게 실천하고 꾸준히 노력하고 있습니다.
              </p>

              <p>
                특히 동대문 국제패션타운에 인접하여 있으며 지하철 2, 3, 4, 5 호선으로
                연결되어있는 대중교통의 편리성으로 사대문 안, 밖에 있는 유명관광 명소와 쇼핑몰, 그리고
                먹거리 장소와의 접근이 용이하여, 다국적인 개인, 가족단위 관광객, 비즈니스 고객으로부터
                호평을 얻고 있습니다.
              </p>

              <p>
                저희 영빈호텔의 임직원들은 항상 고객들의 마음을 먼저 읽고 겸손한 서비스 자세로 다시
                찾아주시는 고객들에게 기쁨을 드릴 수 있도록 노력하겠습니다.
              </p>
            </div>

            {/* Side Image */}
            <div className="w-full md:w-56 flex-shrink-0">
              <div className="w-full h-64 md:h-full min-h-[260px] overflow-hidden rounded-lg">
                <img
                  src="https://public.readdy.ai/ai/img_res/5acd845d409f194bf039c21084392448.jpg"
                  alt="영빈호텔 객실"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          {/* Signature */}
          <div className="mt-12 text-right">
            <p className="text-gray-400 text-xs tracking-widest uppercase mb-1">YOUNGBIN HOTEL</p>
            <p className="text-lg font-bold text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              대표이사 서말남
            </p>
          </div>

          {/* Divider */}
          <div className="mt-16 pt-16 border-t border-stone-100">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                { icon: 'ri-map-pin-2-line', title: '최적의 위치', desc: '동대문 국제패션타운 인접\n지하철 2·3·4·5호선 연결' },
                { icon: 'ri-hotel-bed-line', title: '편안한 객실', desc: '싱글·트윈·더블·온돌 등\n다양한 객실 타입 운영' },
                { icon: 'ri-heart-line', title: '친절한 서비스', desc: '집을 떠난 내집 같은 분위기\n24시간 컨시어지 서비스' },
              ].map((item) => (
                <div key={item.title} className="flex flex-col items-center text-center p-6 bg-stone-50 rounded-lg">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-50 rounded-full mb-4">
                    <i className={`${item.icon} text-amber-700 text-xl`} />
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 mb-2">{item.title}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed whitespace-pre-line">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <Link
              to="/#reservation"
              className="inline-block px-10 py-3.5 bg-amber-700 text-white rounded-full text-sm font-semibold hover:bg-amber-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              실시간 예약하기
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default GreetingPage;
